import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from '../../hooks/use-toast';
import { MapPin, Plus, Archive, Target } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin Quests Tab — list active driver quests + create new ones.
 *
 * A quest is a location-based driver bonus:
 *   "Complete N trips inside <area> within <time_window> → +K{bonus}".
 * Progress is incremented automatically on ride completion (server-side).
 * Bonus is written to the `compensations` collection on completion so it
 * flows through the existing disburse pipeline.
 */
const QuestsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    title: '',
    area_name: '',
    center_lat: -13.989,
    center_lng: 33.787,
    radius_m: 1500,
    min_trips: 3,
    time_window_minutes: 90,
    bonus_mwk: 2000,
    duration_hours: 12,
  });

  const fetchItems = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/quests?status=active`);
      const d = await r.json();
      setItems(d?.items || []);
    } catch {
      // ignore
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleCreate = async () => {
    if (!draft.title.trim() || !draft.area_name.trim()) {
      toast({ title: 'Title and area name are required', variant: 'destructive' });
      return;
    }
    if (!Number.isFinite(draft.center_lat) || !Number.isFinite(draft.center_lng)) {
      toast({ title: 'Valid lat/lng required', variant: 'destructive' });
      return;
    }
    setCreating(true);
    const startsAt = new Date();
    const endsAt = new Date(startsAt.getTime() + draft.duration_hours * 60 * 60 * 1000);
    try {
      const res = await fetch(`${API}/api/admin/quests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: draft.title.trim(),
          area_name: draft.area_name.trim(),
          center_lat: Number(draft.center_lat),
          center_lng: Number(draft.center_lng),
          radius_m: Number(draft.radius_m) || 1500,
          min_trips: Number(draft.min_trips) || 3,
          time_window_minutes: Number(draft.time_window_minutes) || 90,
          bonus_mwk: Number(draft.bonus_mwk) || 0,
          starts_at: startsAt.toISOString(),
          ends_at: endsAt.toISOString(),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || 'Create failed');
      }
      toast({ title: 'Quest published' });
      setDraft((d) => ({ ...d, title: '', area_name: '' }));
      fetchItems();
    } catch (e) {
      toast({ title: 'Create failed', description: String(e.message || e), variant: 'destructive' });
    }
    setCreating(false);
  };

  const handleArchive = async (id) => {
    if (!confirm('Archive this quest? Drivers will stop seeing it.')) return; // eslint-disable-line no-alert
    try {
      await fetch(`${API}/api/admin/quests/${id}`, { method: 'DELETE' });
      fetchItems();
    } catch {
      // ignore
    }
  };

  const fmtMWK = (n) => `K${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  const endsLabel = (iso) => {
    try {
      const d = new Date(iso);
      const diffH = Math.max(0, Math.round((d - Date.now()) / 3600000));
      return diffH < 24 ? `Ends in ${diffH}h` : `Ends ${d.toLocaleDateString()}`;
    } catch {
      return '';
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-quests-tab">
      {/* Create form */}
      <Card className="p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-5 h-5 text-emerald-600" />
          <h3 className="text-sm font-semibold text-gray-900">Publish a new heat-map quest</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Title" className="sm:col-span-2">
            <input
              type="text"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder='e.g. "3 trips in Old Town in 90 min"'
              data-testid="quest-title-input"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              maxLength={80}
            />
          </Field>

          <Field label="Area name (visible to drivers)">
            <input
              type="text"
              value={draft.area_name}
              onChange={(e) => setDraft({ ...draft, area_name: e.target.value })}
              placeholder="e.g. Old Town"
              data-testid="quest-area-input"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              maxLength={60}
            />
          </Field>

          <Field label="Bonus (MWK)">
            <input
              type="number"
              min={100}
              step={500}
              value={draft.bonus_mwk}
              onChange={(e) => setDraft({ ...draft, bonus_mwk: e.target.value })}
              data-testid="quest-bonus-input"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm tabular-nums"
            />
          </Field>

          <Field label="Center latitude">
            <input
              type="number"
              step="0.0001"
              value={draft.center_lat}
              onChange={(e) => setDraft({ ...draft, center_lat: e.target.value })}
              data-testid="quest-lat-input"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm tabular-nums"
            />
          </Field>

          <Field label="Center longitude">
            <input
              type="number"
              step="0.0001"
              value={draft.center_lng}
              onChange={(e) => setDraft({ ...draft, center_lng: e.target.value })}
              data-testid="quest-lng-input"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm tabular-nums"
            />
          </Field>

          <Field label="Radius (m)">
            <input
              type="number"
              min={200}
              max={20000}
              step={100}
              value={draft.radius_m}
              onChange={(e) => setDraft({ ...draft, radius_m: e.target.value })}
              data-testid="quest-radius-input"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm tabular-nums"
            />
          </Field>

          <Field label="Min trips">
            <input
              type="number"
              min={1}
              max={20}
              value={draft.min_trips}
              onChange={(e) => setDraft({ ...draft, min_trips: e.target.value })}
              data-testid="quest-min-trips-input"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm tabular-nums"
            />
          </Field>

          <Field label="Rolling window (minutes)">
            <input
              type="number"
              min={15}
              max={1440}
              step={15}
              value={draft.time_window_minutes}
              onChange={(e) => setDraft({ ...draft, time_window_minutes: e.target.value })}
              data-testid="quest-window-input"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm tabular-nums"
            />
          </Field>

          <Field label="Active for (hours)" className="sm:col-span-2">
            <input
              type="number"
              min={1}
              max={168}
              value={draft.duration_hours}
              onChange={(e) => setDraft({ ...draft, duration_hours: e.target.value })}
              data-testid="quest-duration-input"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm tabular-nums"
            />
          </Field>
        </div>

        <Button
          onClick={handleCreate}
          disabled={creating}
          data-testid="quest-create-btn"
          className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          {creating ? 'Publishing…' : 'Publish quest'}
        </Button>
      </Card>

      {/* Active quests list */}
      <Card className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">Active quests</h3>
          <span className="text-xs text-gray-500">{items.length} active</span>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500 py-8 text-center">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-500 py-8 text-center" data-testid="admin-quests-empty">
            No active quests yet. Publish your first one above.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((q) => (
              <li key={q.id} className="py-3 flex items-start justify-between gap-3" data-testid="admin-quest-row">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">{q.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" /> {q.area_name} · {q.radius_m}m radius · {q.min_trips} trips in {q.time_window_minutes}m
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">{endsLabel(q.ends_at)}</p>
                </div>
                <span className="shrink-0 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[12px] font-bold tabular-nums">
                  +{fmtMWK(q.bonus_mwk)}
                </span>
                <button
                  type="button"
                  onClick={() => handleArchive(q.id)}
                  data-testid="admin-quest-archive"
                  className="shrink-0 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-rose-600"
                >
                  <Archive className="w-3.5 h-3.5" />
                  Archive
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};

const Field = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">{label}</span>
    {children}
  </label>
);

export default QuestsTab;
